import {
  COMPONENT_TYPE_RADIOBUTTON,
  COMPONENT_TYPE_CHECKBOX,
  COMPONENT_TYPE_BUTTON,
  COMPONENT_TYPE_GUEST,
  COMPONENT_TYPE_IMAGE,
  COMPONENT_TYPE_TEXT,
  DEFAULT_GUESTS,
  LEAD_COLLECTOR_STEPS,
  ElementTypeEnum,
  FormComponent,
  OptionInfo,
  VIEW_TYPE,
  GuestInfo,
  FormData,
  Form,
  validationFieldsGuest,
  FormElementSidebar,
  FormErrorToast,
  FormCanvas,
} from '@entities/leadCollector';
import { updateForm, UpdateFormPayload } from '@shared/api/lead-collector';
import { CreateForm, FormTemplateHeader } from '@features/leadCollector';
import { REQUIRED_FIELD_MESSAGE } from '@shared/constants/errorMessages';
import { SidebarRenderContent } from './SidebarRenderContent';
import { SUCCESS_CODE } from '@shared/constants/codeStatuses';
import { useToast } from '@shared/hooks/useToast';
import { useEffect, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { PageHeader } from './PageHeader';
import { FormHeader } from './FormHeader';
import { styles } from './styles';

interface CreateFormTemplateProps {
  setStep: (step: number) => void;
  form?: Form;
  viewType: number;
  setViewType: (step: number) => void;
  hasGeneratedLinks: boolean;
  prevStepFromCreate: number;
  isLoadingForm: boolean;
  refetchForm: () => void;
}

export const CreateFormTemplate = ({
  setStep,
  form,
  viewType,
  setViewType,
  prevStepFromCreate,
  isLoadingForm,
  refetchForm,
}: CreateFormTemplateProps) => {
  const [formData, setFormData] = useState<FormData>({ components: [] });
  const [copyData, setCopyData] = useState<FormData>(structuredClone(formData));
  const [formName, setFormName] = useState(form?.name || '');
  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const { showToast } = useToast();

  const handleGuestClick = (guestId: string | number) => {
    const hasGuestCard = formData.components.some((component) => component.type === COMPONENT_TYPE_GUEST);

    if (hasGuestCard) {
      showToast(<FormErrorToast errorText="Карточка гостя уже добавлена на рабочую область" />, 'error');
      return;
    }

    const newComponent: FormComponent = {
      id: String(guestId),
      instanceId: `guest-${Date.now()}`,
      type: COMPONENT_TYPE_GUEST,
      properties: { ...structuredClone(DEFAULT_GUESTS) },
    };

    setFormData((prev) => ({
      components: [...prev.components, newComponent],
    }));
  };

  const handleTextClick = (textId: number) => {
    const newComponent: FormComponent = {
      id: String(textId),
      instanceId: `text-${Date.now()}`,
      type: COMPONENT_TYPE_TEXT,
      properties: { html: '' },
    };

    setFormData((prev) => ({
      components: [...prev.components, newComponent],
    }));
  };

  const handleCheckboxClick = (checkboxId: number) => {
    const newComponent: FormComponent = {
      id: String(checkboxId),
      instanceId: `checkbox-${Date.now()}`,
      type: COMPONENT_TYPE_CHECKBOX,
      properties: {},
    };

    setFormData((prev) => ({
      components: [...prev.components, newComponent],
    }));
  };

  const handleRadioButtonClick = (radioButtonClickId: number) => {
    const newComponent: FormComponent = {
      id: String(radioButtonClickId),
      instanceId: `radiobutton-${Date.now()}`,
      type: COMPONENT_TYPE_RADIOBUTTON,
      properties: {},
    };

    setFormData((prev) => ({
      components: [...prev.components, newComponent],
    }));
  };

  const handleButtonClick = (buttonId: number) => {
    const newComponent: FormComponent = {
      id: String(buttonId),
      instanceId: `button-${Date.now()}`,
      type: COMPONENT_TYPE_BUTTON,
      properties: { textButton: '' },
    };

    setFormData((prev) => ({
      components: [...prev.components, newComponent],
    }));
  };

  const handleDeleteComponent = (instanceId: string) => {
    setFormData((prev) => ({
      components: prev.components.filter((component) => component.instanceId !== instanceId),
    }));
  };

  const handleReorderComponents = (dragIndex: number, dropIndex: number) => {
    setFormData((prev) => {
      const newComponents = [...prev.components];
      [newComponents[dragIndex], newComponents[dropIndex]] = [newComponents[dropIndex], newComponents[dragIndex]];
      return { components: newComponents };
    });
  };

  const onChangeProperties = (index: string, properties: Record<string, unknown>) => {
    const componentIndex = formData.components.findIndex(({ instanceId }) => instanceId === index);

    if (componentIndex === -1) return;

    setFormData((prev) => ({
      ...prev,
      components: prev.components.map((component, index) =>
        index === componentIndex ? { ...component, properties } : component
      ),
    }));
  };

  const onSaveForm = async (newData?: Partial<UpdateFormPayload>) => {
    const bodyForm = structuredClone({ ...formData });

    if (!validateForm(bodyForm.components)) return;

    const guest = bodyForm.components.find(({ type }) => type === COMPONENT_TYPE_GUEST);

    if (!form?.maxExtraAdults) {
      guest!.properties.adultGuest = null;
    }

    if (!form?.maxExtraChildren) {
      guest!.properties.childrenGuest = null;
    }

    const images = bodyForm.components.filter(({ type }) => type === COMPONENT_TYPE_IMAGE);

    const files: File[] = [];

    for (const image of images) {
      const fileImage = image.properties.file as File;
      const imageSrc = image.properties.src;
      const uploadImage = image.properties.uploadImage;

      if (!fileImage && !imageSrc) {
        showToast(<FormErrorToast errorText="Не все изображения загружены" />, 'error');
        return;
      }

      if (fileImage) {
        const renamedFile = new File([fileImage], image.instanceId);
        files.push(renamedFile);
      }

      image.properties = { uploadImage: uploadImage || image.instanceId, src: imageSrc || '' };
    }

    const data = {
      id: form?.id || 0,
      data: {
        name: newData?.data?.name || formName,
        max_extra_children: newData?.data?.max_extra_children ?? (form?.maxExtraChildren || 0),
        max_extra_adults: newData?.data?.max_extra_adults ?? (form?.maxExtraAdults || 0),
        body_form: bodyForm,
      },
      files,
    };

    const response = await updateForm(data);

    if (response.request.response.includes('already exist')) {
      !newData && showToast(<FormErrorToast errorText="Шаблон с таким названием уже существует" />, 'error');
      return 'Шаблон с таким названием уже существует';
    }

    if (response.request.response.includes('have generated links')) {
      showToast(<FormErrorToast errorText="Редактирование запрещено, форма уже отправлена участникам" />, 'error');
      return;
    }

    if (response.status !== SUCCESS_CODE) {
      showToast(<FormErrorToast errorText={response.request.response} />, 'error');
      return;
    }

    refetchForm();

    if (newData) {
      setIsOpenSettings(false);
      return;
    }

    setStep(LEAD_COLLECTOR_STEPS.CHOOSE_FORM);
  };

  const onSendForm = async () => {
    const components = formData.components;

    for (const component of components) {
      switch (component.type) {
        case COMPONENT_TYPE_CHECKBOX: {
          const options = component.properties.options as OptionInfo[];
          if (!options) return;

          for (const option of options) {
            if (option.isActive) continue;
            option.error = REQUIRED_FIELD_MESSAGE;
          }

          break;
        }
        case COMPONENT_TYPE_RADIOBUTTON: {
          const options = component.properties.options as OptionInfo[];
          if (!options) return;

          const hasActive = options.some(({ isActive }) => isActive);

          if (hasActive) break;

          for (const option of options) {
            option.error = REQUIRED_FIELD_MESSAGE;
          }

          break;
        }
        case COMPONENT_TYPE_GUEST: {
          const guests = component.properties;
          if (!guests) return;

          await validationFieldsGuest(guests.mainGuest as GuestInfo);

          if (guests.adultGuest) {
            for (const guest of guests.adultGuest as GuestInfo[]) {
              await validationFieldsGuest(guest);
            }
          }

          if (guests.childrenGuest) {
            for (const guest of guests.childrenGuest as GuestInfo[]) {
              await validationFieldsGuest(guest);
            }
          }

          break;
        }
      }
    }

    setFormData({ ...formData });
  };

  const onPreviewTemplate = (newViewType: number) => {
    switch (viewType) {
      case VIEW_TYPE.CONSTRUCTOR:
        setCopyData(structuredClone(formData));
        if (!validateForm(formData.components)) return;
        setFormData({ ...formData });
        break;
      case VIEW_TYPE.PREVIEW_CONSTRUCTOR:
        setFormData(structuredClone(copyData));
        break;
      case VIEW_TYPE.PREVIEW_TEMPLATE: {
        const newFormData = { components: form?.bodyForm?.components || [] };
        setFormData(newFormData);
        break;
      }
    }

    setViewType(newViewType);
  };

  const validateForm = (components: FormComponent[]) => {
    const hasButton = components.some(({ type }) => type === COMPONENT_TYPE_BUTTON);

    if (!hasButton) {
      showToast(<FormErrorToast errorText="Форма должна содержать кнопку действия" />, 'error');
      return false;
    }

    const guest = components.find(({ type }) => type === COMPONENT_TYPE_GUEST);

    if (!guest) {
      showToast(<FormErrorToast errorText='Форма должна содержать блок "Карточка гостя”' />, 'error');
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (viewType === VIEW_TYPE.PREVIEW_CONSTRUCTOR) return;
    setFormName(form?.name || '');
    setFormData({ components: structuredClone(form?.bodyForm?.components) || [] });
  }, [form?.name]);

  if (isOpenSettings) {
    return <CreateForm form={form} onSave={onSaveForm} onCancel={() => setIsOpenSettings(false)} setStep={setStep} />;
  }

  if (viewType !== VIEW_TYPE.CONSTRUCTOR) {
    return (
      <div {...stylex.props(styles.previewContainer)}>
        <PageHeader isPreviewTemplate={viewType === VIEW_TYPE.PREVIEW_TEMPLATE} />
        <FormHeader
          title={form?.name}
          onPreviewTemplate={onPreviewTemplate}
          viewType={viewType}
          setStep={setStep}
          hasGeneratedLinks={!!form?.hasGeneratedLinks}
          formId={form?.id}
          prevStep={prevStepFromCreate}
        />
        <FormCanvas
          formData={formData}
          onDeleteComponent={handleDeleteComponent}
          onReorder={handleReorderComponents}
          onChangeProperties={onChangeProperties}
          isCanEdit={viewType !== VIEW_TYPE.CONSTRUCTOR}
          form={form}
          onSaveForm={onSendForm}
          isLoadingForm={isLoadingForm}
        />
      </div>
    );
  }

  return (
    <div {...stylex.props(styles.container)}>
      <FormTemplateHeader
        onBack={() => setStep(LEAD_COLLECTOR_STEPS.CHOOSE_FORM)}
        formName={formName}
        onChangeFormName={setFormName}
        onPreviewTemplate={onPreviewTemplate}
        isDisabledActions={!formData.components.length}
        onSaveForm={() => onSaveForm()}
        setIsOpenSetting={setIsOpenSettings}
      />

      <div {...stylex.props(styles.content)}>
        <FormElementSidebar
          handlers={{
            [ElementTypeEnum.TEXT]: handleTextClick,
            [ElementTypeEnum.FORMS]: handleGuestClick,
            [ElementTypeEnum.CHECKBOX]: handleCheckboxClick,
            [ElementTypeEnum.RADIOBUTTON]: handleRadioButtonClick,
            [ElementTypeEnum.BUTTON]: handleButtonClick,
          }}
          renderContent={(elementType, elements) => (
            <SidebarRenderContent elements={elements} elementType={elementType} setFormData={setFormData} />
          )}
        />

        <FormCanvas
          formData={formData}
          onDeleteComponent={handleDeleteComponent}
          onReorder={handleReorderComponents}
          onChangeProperties={onChangeProperties}
          isCanEdit={viewType !== VIEW_TYPE.CONSTRUCTOR}
          form={form}
          onSaveForm={onSendForm}
          isLoadingForm={isLoadingForm}
        />
      </div>
    </div>
  );
};
